import { ISiteLocation, LocationType } from '~/client/graph'
import { locationTypeFromKind } from '~/client/src/shared/resources/domain/locationKind'

/**
 * The live kind of the location with an id:
 *
 * - a kind slug when this client holds the location and it has a kind;
 * - `null` when it holds it but it has none;
 * - `undefined` when it does not hold the id.
 *
 * LocationAttributesStore.getLocationRootKindById is the app's implementation.
 */
export type LocationKindResolver = (id: string) => string | null | undefined

/** The location fields the delivery form shows for where a delivery goes, in form order. */
export const DELIVERY_TO_LOCATION_SLOTS: readonly LocationType[] = [
  LocationType.Building,
  LocationType.Zone,
  LocationType.Area,
  LocationType.Gate,
  LocationType.Level,
  LocationType.Route,
  LocationType.Staging,
  LocationType.InteriorDoor,
  LocationType.InteriorPath,
]

/** The location fields it shows for where a delivery comes from: the kinds with a From field. */
export const DELIVERY_FROM_LOCATION_SLOTS: readonly LocationType[] = [
  LocationType.Building,
  LocationType.Zone,
  LocationType.Area,
  LocationType.Level,
  LocationType.Staging,
]

/**
 * The slot a delivery location entry belongs in.
 *
 * The live location's kind when the resolver knows one, because the entry's `type` is a
 * snapshot of the kind at save time and goes stale on a re-kind (STX1-597). A
 * project-added kind maps to no legacy type, so it belongs in no slot. Otherwise - the
 * location is not held here, or has no kind - the stored type, as before.
 */
export function getDeliveryLocationSlotType(
  location: ISiteLocation | null | undefined,
  resolveKind: LocationKindResolver,
): LocationType | null {
  if (!location) return null

  const liveKind = location.id ? resolveKind(location.id) : undefined
  if (liveKind) return locationTypeFromKind(liveKind)

  return location.type ?? null
}

export interface IDeliveryLocationSlots {
  readonly bySlot: ReadonlyMap<LocationType, ISiteLocation>
  readonly unslotted: readonly ISiteLocation[]
}

/**
 * A delivery's location entries assigned to the slots the form shows.
 *
 * - Each entry is a candidate for the slot {@link getDeliveryLocationSlotType} gives it.
 * - Within a slot, the first candidate whose stored type already IS that slot wins, else
 *   the first candidate. So for entries never re-kinded every slot holds exactly what it
 *   did when slots were keyed by stored type.
 * - `unslotted` is every other location the form would otherwise lose, once each: one
 *   that fits no slot (a project-added or unclassified kind, a vertical object, a site
 *   root), and one that lost its slot - a re-kind collision. A loser stored as its slot's
 *   kind is kept too: Graph rewrites every snapshot type to the live kind on each delivery
 *   write, so after one write a re-kind loser is stored exactly like the winner. An entry
 *   whose location a winner or an earlier entry already holds is a true duplicate, and is
 *   left out.
 */
export function assignDeliveryLocationSlots(
  locations: readonly ISiteLocation[] | null | undefined,
  slots: readonly LocationType[],
  resolveKind: LocationKindResolver,
): IDeliveryLocationSlots {
  const entries = (locations ?? []).filter(Boolean)
  const slotSet = new Set(slots)

  const candidatesBySlot = new Map<LocationType, ISiteLocation[]>()
  entries.forEach(location => {
    const slot = getDeliveryLocationSlotType(location, resolveKind)
    if (!slot || !slotSet.has(slot)) return

    const candidates = candidatesBySlot.get(slot)
    if (candidates) {
      candidates.push(location)
    } else {
      candidatesBySlot.set(slot, [location])
    }
  })

  const bySlot = new Map<LocationType, ISiteLocation>()
  candidatesBySlot.forEach((candidates, slot) => {
    bySlot.set(
      slot,
      candidates.find(location => location.type === slot) ?? candidates[0],
    )
  })

  const heldIds = new Set(Array.from(bySlot.values(), location => location.id))
  const unslotted = entries.filter(location => {
    // Nothing to carry without an id: SiteLocationInput.id is non-null.
    if (!location.id || heldIds.has(location.id)) return false

    heldIds.add(location.id)
    return true
  })

  return { bySlot, unslotted }
}

/**
 * A form save's slot entries, followed by the entries no slot showed, so saving the form
 * keeps them. An unslotted entry is skipped when its id is already present: a slot field
 * now holds that location, or an earlier entry carried it.
 */
export function withUnslottedLocations(
  slotted: ISiteLocation[],
  unslotted: readonly ISiteLocation[],
): ISiteLocation[] {
  const ids = new Set(slotted.map(location => location.id))
  const carried = unslotted.filter(location => {
    if (ids.has(location.id)) return false

    ids.add(location.id)
    return true
  })

  return [...slotted, ...carried]
}
